/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/solid'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import { ReservationPlan } from 'types/entities/ReservationPlan'
import { useAppDispatch } from 'helpers/store'
import ReservationsAction from 'store/reservations/ReservationsAction'
import { toast } from 'utils/toastUtils'
import { closeModal, closeSecondaryModal } from 'helpers/actions'
import ReservationPlannerModalItem from './ReservationPlannerModalComponents/ReservationPlannerModalItem'

interface ReservationPlannerModalProps {
    reservationId: number
    initialPlans?: ReservationPlan[]
    title?: string
}

const ReservationPlannerModal: React.FC<ReservationPlannerModalProps> = ({
    reservationId,
    initialPlans = [],
    title,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [plans, setPlans] = useState<Partial<ReservationPlan>[]>(
        initialPlans.length > 0 ? initialPlans : [{ name: '', time: 0 }]
    )

    const handleAddRow = () => {
        setPlans([...plans, { name: '', time: 0 }])
    }

    const handleRemoveRow = (index: number) => {
        setPlans(plans.filter((_, i) => i !== index))
    }

    const handleChange = <K extends keyof ReservationPlan>(
        index: number,
        field: K,
        value: ReservationPlan[K]
    ) => {
        const newPlans = [...plans]
        newPlans[index] = { ...newPlans[index], [field]: value }
        setPlans(newPlans)
    }

    const formatTimeToHHMM = (minutes: number) => {
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
    }

    const parseHHMMToMinutes = (hhmm: string) => {
        const [h, m] = hhmm.split(':').map(Number)
        return (h || 0) * 60 + (m || 0)
    }

    const handleSave = () => {
        const validPlans = plans
            .filter((p) => p.name && p.name.trim() !== '')
            .map((p, i) => ({
                ...p,
                id: p.id || i + 1,
                reservation_id: reservationId,
                is_active: true,
            })) as ReservationPlan[]

        dispatch(
            ReservationsAction.fetchSetReservationPlan(reservationId, validPlans)
        )
            .then(() => {
                toast.success(t('Reservation plan saved'))
                dispatch(closeSecondaryModal())
            })
            .catch(() => {
                toast.error(t('Failed to save reservation plan'))
            })
    }

    return (
        <UiFormModalShell
            className="ReservationPlannerModal"
            title={title || t('Reservation Planner')}
            showHeader={true}
            footer={
                <UiFormActionsBar
                    primaryText={t('Save')}
                    onPrimary={handleSave}
                    secondaryText={t('Cancel')}
                    onSecondary={() => dispatch(closeSecondaryModal())}
                />
            }
        >
            <div className="ReservationPlannerModal flex flex-col gap-2">
                <div className="grid grid-cols-12 gap-2 font-semibold text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <div className="col-span-8 px-1">{t('Activity Name')}</div>
                    <div className="col-span-3 px-1">{t('Time')}</div>
                    <div className="col-span-1"></div>
                </div>
                <div className="flex flex-col gap-1.5">
                    {plans.map((plan, index) => (
                        <ReservationPlannerModalItem
                            key={index}
                            index={index}
                            plan={plan}
                            formatTimeToHHMM={formatTimeToHHMM}
                            parseHHMMToMinutes={parseHHMMToMinutes}
                            handleChange={handleChange}
                            handleRemoveRow={handleRemoveRow}
                        />
                    ))}
                </div>
                <div className="flex justify-start mt-1">
                    <UiButton
                        onClick={handleAddRow}
                        style={UiButtonStyle.Secondary}
                        size={UiButtonSize.Small}
                        icon={<PlusIcon className="w-4 h-4" />}
                    >
                        {t('Add Row')}
                    </UiButton>
                </div>
            </div>
        </UiFormModalShell>
    )
}

export default ReservationPlannerModal
